import {
  Stats,
  UserActivityPracticeWeek,
  UserLearnedWordMonth,
} from '@beconfident/api-lib';
import axios from 'axios';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';

import Environment from '@/environment';
import notifyError from '@/helpers/notify-error';
import logger from '@/infra/logger';
import { AppInfo, AppPlatform, AppRegisterLead } from '@/types/app';

type State = {
  platform: AppPlatform;
  info: AppInfo;
  needsUpdate: boolean;
  showLogin: boolean;
  showPaywall: boolean;
  showCallManagerTutorial: boolean;
  showVideoManagerTutorial: boolean;
  chatManagerTutorial: {
    showFullTutorial: boolean;
    showAudioWaveTutorial: boolean;
    showInteractionScoreTutorial: boolean;
  };
  showGoalsActivityTutorial: boolean;
  showNavigation: boolean;
  clickGoBack: boolean;
  viewPortHeight: string;
  fullscreenLoading: {
    theme?: 'dark' | 'light';
    color?: string;
    show: boolean;
    texts: string[];
  };
  // todo:vite
  ranking: any;
  stats: Stats;
  maxStreak: number;
  wordsLearnedByMonth: UserLearnedWordMonth[];
  activityPracticeByWeek: UserActivityPracticeWeek[];
  likert: {
    show: boolean;
    lastSubmit: Date | null;
  };
  streak: {
    lastShown: string | null;
  };
  isReadyForAsyncAudioRecording: boolean;
};

export const useAppStore = defineStore('app', {
  persist: true,
  state: (): State => ({
    platform: AppPlatform.Undefined,
    info: {
      build: '',
      version: '',
      package: '',
    },
    needsUpdate: false,
    showLogin: false,
    showPaywall: false,
    showCallManagerTutorial: true,
    showVideoManagerTutorial: true,
    chatManagerTutorial: {
      showFullTutorial: true,
      showAudioWaveTutorial: true,
      showInteractionScoreTutorial: true,
    },
    showGoalsActivityTutorial: true,
    showNavigation: true,
    clickGoBack: false,
    viewPortHeight: '100%',
    fullscreenLoading: {
      show: false,
      texts: [],
    },
    ranking: [],
    stats: {
      placementTest: null,
      conversationMinutesSpent: {
        lastMonthMinutes: 0,
        lastWeekMinutes: 0,
        totalMinutes: 0,
      },
      lesson: {
        levelProgress: 0,
        totalProgress: 0,
      },
      proficiencyLevel: '',
      streak: 0,
      wordsLearned: {
        lastMonth: 0,
        lastWeek: 0,
        total: 0,
      },
      activity: {
        totalFinished: 0,
      },
      newCertificatesCount: 0,
    },
    maxStreak: 0,
    wordsLearnedByMonth: [],
    activityPracticeByWeek: [],
    likert: {
      show: false,
      lastSubmit: null,
    },
    streak: {
      lastShown: null,
    },
    isReadyForAsyncAudioRecording: false,
  }),

  actions: {
    setPlatform(platform: AppPlatform) {
      this.platform = platform;
    },
    setInfo(info: AppInfo) {
      this.info = info;
    },
    setNeedsUpdate(needsUpdate: boolean) {
      this.needsUpdate = needsUpdate;
    },
    setShowLogin(showLogin: boolean) {
      this.showLogin = showLogin;
    },
    setShowPaywall(showPaywall: boolean) {
      this.showPaywall = showPaywall;
    },
    setShowCallManagerTutorial(newState: boolean) {
      this.showCallManagerTutorial = newState;
    },
    setShowVideoManagerTutorial(newState: boolean) {
      this.showVideoManagerTutorial = newState;
    },
    setChatManagerFullTutorial(newState: boolean) {
      this.chatManagerTutorial.showFullTutorial = newState;
    },
    setAudioWaveTutorial(newState: boolean) {
      this.chatManagerTutorial.showAudioWaveTutorial = newState;
    },
    setInteractionScoreTutorial(newState: boolean) {
      this.chatManagerTutorial.showInteractionScoreTutorial = newState;
    },
    setShowGoalsActivityTutorial(newState: boolean) {
      this.showGoalsActivityTutorial = newState;
    },
    setShowNavigation(showNavigation: boolean) {
      this.showNavigation = showNavigation;
    },
    setFullscreenLoading({
      show,
      texts,
      color = 'brand-01',
      theme,
    }: {
      show: boolean;
      texts: string[];
      color?: string;
      theme?: 'light' | 'dark';
    }) {
      this.fullscreenLoading = {
        show,
        texts,
        color,
        theme,
      };
    },
    setClickGoBack(clickGoBack: boolean) {
      this.clickGoBack = clickGoBack;
    },
    setStats(stats: Stats) {
      this.stats = stats;
    },
    setMaxStreak(maxStreak: number) {
      this.maxStreak = maxStreak;
    },
    // todo:vite
    setRanking(ranking: any) {
      this.ranking = ranking;
    },
    setWordsLearnedByMonth(words: UserLearnedWordMonth[]) {
      this.wordsLearnedByMonth = words;
    },
    setActivityPracticeByMonth(activity: UserActivityPracticeWeek[]) {
      this.activityPracticeByWeek = activity;
    },
    setLikert({ show, lastSubmit }: { show: boolean; lastSubmit?: Date }) {
      this.likert = {
        show,
        lastSubmit: lastSubmit ?? this.likert.lastSubmit,
      };
    },
    setViewPortHeight(value: string) {
      this.viewPortHeight = value;
    },
    setIsReadyForAsyncAudioRecording(isReadyForAsyncAudioRecording: boolean) {
      this.isReadyForAsyncAudioRecording = isReadyForAsyncAudioRecording;
    },

    async getHealth() {
      const { data } = await axios.get('/status');
      this.setNeedsUpdate(false);

      return data;
    },

    async registerLead(dataLead: AppRegisterLead) {
      try {
        const { data } = await axios.post(
          '/site/lead/start-conversation',
          dataLead,
        );

        return data;
      } catch (error) {
        logger.captureException(error);

        return error;
      }
    },

    async getStats() {
      try {
        const response = await this.$apiClient.getStats();

        this.setStats(response);

        return response;
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Não foi possível carregar os seus dados');
      }
    },
    async getRanking() {
      try {
        const response = await this.$apiClient.getRanking();

        this.setRanking(response);

        return response;
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Não foi possível carregar o ranking');
      }
    },

    async getReferrals() {
      return this.$apiClient.listReferrals();
    },

    async getStatsWords() {
      try {
        const response = await this.$apiClient.getWordsLearnedByMonths();
        this.setWordsLearnedByMonth(response.wordsLearnedByMonth);

        return response.wordsLearnedByMonth;
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Não foi possível carregar as suas indicações');
      }
    },

    async getStatsActivity() {
      try {
        const response = await this.$apiClient.getActivityPracticeByWeek();
        this.setActivityPracticeByMonth(response.activityByWeek);

        return response.activityByWeek;
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Não foi possível carregar as suas indicações');
      }
    },

    getPlatformMap(platform: string): AppPlatform {
      const platformMap: { [key: string]: AppPlatform } = {
        android: AppPlatform.Android,
        ios: AppPlatform.Ios,
        web: AppPlatform.Web,
      };

      return platformMap[platform];
    },

    async verifyIfNeedsToShowStreak() {
      const { lastStreakDay } = await this.$apiClient.getMeStreak();

      const today = dayjs().format('YYYY-MM-DD');

      const isANewStreak = this.streak.lastShown !== lastStreakDay;
      const lastStreakIsToday = lastStreakDay === today;

      return isANewStreak && lastStreakIsToday;
    },
  },

  getters: {
    isLocal() {
      // @ts-ignore
      return !this.isProduction && !this.isStaging;
    },
    isStaging() {
      return Boolean(Environment.API_URL.includes('staging.beconfident.app'));
    },
    isProduction() {
      return Boolean(Environment.API_URL.includes('api.beconfident.app'));
    },
    isApp(): boolean {
      return this.platform && this.platform !== AppPlatform.Web;
    },
    homePagePath() {
      // @ts-ignore
      return this.isApp ? '/home' : '/settings';
    },
  },
});
